// Homepage widget registry.
//
// A widget is a simplified, tile-sized version of a full page. Every page in
// the favorites catalog is automatically widget-capable: pages listed in
// LIVE_WIDGETS render a live data summary, and every other page auto-gets a
// GenericPageWidget shortcut tile — so a newly added page needs nothing
// beyond its catalog entry to show up in the widget picker. When a new page
// has data worth summarizing, add a component here (see CLAUDE.md).
import { favoritesCatalog } from "../favoritesCatalog";
import WeatherWidget from "./WeatherWidget";
import LeadTimesWidget from "./LeadTimesWidget";
import WebLeadsWidget from "./WebLeadsWidget";
import OrderProblemsWidget from "./OrderProblemsWidget";
import FeatureRequestsWidget from "./FeatureRequestsWidget";
import SbcfWidget from "./SbcfWidget";
import MyExteriorOrdersWidget from "./MyExteriorOrdersWidget";
import MyExteriorRequestsWidget from "./MyExteriorRequestsWidget";
import RequestManagerWidget from "./RequestManagerWidget";
import MyWindowFormsWidget from "./MyWindowFormsWidget";
import AssignedWindowFormsWidget from "./AssignedWindowFormsWidget";
import WindowQuoteManagerWidget from "./WindowQuoteManagerWidget";
import MyFormsWidget from "./MyFormsWidget";
import MySubmissionsWidget from "./MySubmissionsWidget";
import MyFeatureRequestsWidget from "./MyFeatureRequestsWidget";
import CabinetQuotesWidget from "./CabinetQuotesWidget";
import ServiceRequestManagerWidget from "./ServiceRequestManagerWidget";
import MyServiceRequestsWidget from "./MyServiceRequestsWidget";
import MessagesWidget from "./MessagesWidget";
import PCWWidget, { MillShopWidget } from "./PCWWidget";
import ToolboxTalksWidget from "./ToolboxTalksWidget";
import SafetyOverviewWidget, {
  FireExtinguishersWidget,
  SafetyConcernsWidget,
  ToolboxResultsWidget,
  TrainingVideosWidget,
} from "./SafetyWidgets";
import SignageWidget from "./SignageWidget";
import AppCodesWidget from "./AppCodesWidget";
import AssistantUsageWidget from "./AssistantUsageWidget";
import MonthlyFeedbackWidget from "./MonthlyFeedbackWidget";
import UserManagerWidget, { PageAccessWidget } from "./AccessWidgets";
import FleetWidget, {
  ForkliftsWidget,
  InspectionLogWidget,
  VehiclesWidget,
} from "./FleetWidget";

// Live widgets keyed by the path of the page they summarize. Pages absent
// here fall back to the generic shortcut tile — reserve that for pages with
// nothing to summarize (pure forms, settings/builders, docs, external
// tools); see CLAUDE.md.
const LIVE_WIDGETS = {
  "/dashboard/weather": WeatherWidget,
  "/windowdoor/lead-times": LeadTimesWidget,
  "/webleads": WebLeadsWidget,
  "/order-problems": OrderProblemsWidget,
  "/admin/feature-requests": FeatureRequestsWidget,
  "/sbcf": SbcfWidget,
  // Role-adaptive: same widget serves the sales view and the team queue.
  "/sbcfadmin": SbcfWidget,
  "/windowdoor/exteriororders": MyExteriorOrdersWidget,
  "/windowdoor/requests": MyExteriorRequestsWidget,
  "/windowdoor/admin/requestlist": RequestManagerWidget,
  "/windowdoor/sales/windowforms": MyWindowFormsWidget,
  "/windowdoor/staff/windowforms": AssignedWindowFormsWidget,
  "/windowdoor/quote-manager": WindowQuoteManagerWidget,
  "/windowdoor/service-requests": ServiceRequestManagerWidget,
  "/windowdoor/my-service-requests": MyServiceRequestsWidget,
  "/messages": MessagesWidget,
  "/forms/viewall": MyFormsWidget,
  "/forms/my-submissions": MySubmissionsWidget,
  "/forms/feature-request": MyFeatureRequestsWidget,
  "/cabinet-quotes": CabinetQuotesWidget,
  "/pcw": PCWWidget,
  "/mill-shop": MillShopWidget,
  "/safety/toolbox-talks": ToolboxTalksWidget,
  "/safety/overview": SafetyOverviewWidget,
  "/safety/concerns": SafetyConcernsWidget,
  "/safety/extinguishers": FireExtinguishersWidget,
  "/safety/training": TrainingVideosWidget,
  "/safety/toolbox-results": ToolboxResultsWidget,
  "/signage": SignageWidget,
  "/admin/app-codes": AppCodesWidget,
  "/admin/assistant-usage": AssistantUsageWidget,
  "/admin/feedback": MonthlyFeedbackWidget,
  "/admin/user/userManager": UserManagerWidget,
  "/admin/access": PageAccessWidget,
  "/fleet": FleetWidget,
  "/fleet/forklifts": ForkliftsWidget,
  "/fleet/vehicles": VehiclesWidget,
  "/fleet/inspections": InspectionLogWidget,
};

// Same section grouping as the favorites picker; `live` marks pages with a
// real data widget so the picker can badge them.
export const widgetCatalog = favoritesCatalog.map((section) => ({
  label: section.label,
  items: section.items.map((item) => ({
    ...item,
    group: section.label,
    live: Boolean(LIVE_WIDGETS[item.path]),
  })),
}));

export const widgetComponentFor = (path) => LIVE_WIDGETS[path] || null;
